<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

type Option = {
  resource_id: number
  resource_name: string
  duration_min: number
  ends_at: string
  price_cents: number
  per_player_cents: number | null
  remaining_seats: number
  shared: boolean
  indoor: boolean | null
  court_type: string | null
  any_resource?: boolean
  available_resources?: number
}
type Start = { start: string; time: string; time_label: string; zone: string; utc_offset: string; options: Option[]; any_options: Option[] }
type Payload = {
  venue: { name: string; slug: string; time_zone: string; currency: string }
  selected_date: string
  dates: { date: string; available: boolean }[]
  nearest: { date: string; time: string } | null
  categories: { value: string; label: string }[]
  filters: { durations: number[]; indoors: boolean[]; court_types: string[] }
  starts: Start[]
}

const props = defineProps<{
  endpoint: string
  bookingUrl: string
  initialFilters?: Record<string, string>
}>()
const { t, locale } = useI18n()
const loading = ref(true)
const refreshing = ref(false)
const error = ref(false)
const payload = ref<Payload | null>(null)
const selected = ref<Option & { start: string; time: string } | null>(null)
const mode = ref<'any' | 'resource'>('any')
const filters = ref<Record<string, string>>({ ...(props.initialFilters || {}) })
let timer: number | undefined
let requestSequence = 0
let requestController: AbortController | undefined

const currencyFormatter = computed(() => new Intl.NumberFormat(locale.value, {
  style: 'currency', currency: payload.value?.venue.currency || 'EUR', maximumFractionDigits: 2,
}))

const checkoutUrl = computed(() => {
  if (!selected.value || !payload.value) return props.bookingUrl
  const url = new URL(props.bookingUrl, window.location.origin)
  url.searchParams.set('venue', payload.value.venue.slug)
  url.searchParams.set('resource_id', String(selected.value.resource_id))
  url.searchParams.set('starts_at', selected.value.start)
  url.searchParams.set('duration_min', String(selected.value.duration_min))
  if (selected.value.any_resource) {
    url.searchParams.set('any_resource', '1')
    url.searchParams.set('shown_price_cents', String(selected.value.price_cents))
    ;['sport', 'indoor', 'court_type'].forEach((name) => {
      const value = filters.value[name]
      if (value) url.searchParams.set(name, value)
    })
  }
  return `${url.pathname}${url.search}`
})

function money(cents: number) {
  return currencyFormatter.value.format(cents / 100)
}

function queryUrl() {
  const url = new URL(props.endpoint, window.location.origin)
  Object.entries(filters.value).forEach(([key, value]) => value && url.searchParams.set(key, value))
  return url
}

async function load(silent = false) {
  const sequence = ++requestSequence
  requestController?.abort()
  const controller = new AbortController()
  requestController = controller
  if (silent) refreshing.value = true
  else loading.value = true
  error.value = false
  try {
    const response = await fetch(queryUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal })
    if (!response.ok) throw new Error(`availability ${response.status}`)
    const nextPayload = await response.json() as Payload
    if (sequence !== requestSequence) return
    payload.value = nextPayload
    filters.value.date = payload.value.selected_date
    if (!selected.value && filters.value.start) {
      const requested = payload.value.starts.find((start) => start.start === filters.value.start)
      const option = requested?.any_options[0]
      if (requested && option) choose(requested, option)
    }
    if (selected.value && !payload.value.starts.some((start) =>
      start.start === selected.value?.start && [...start.options, ...start.any_options].some((option) =>
        option.duration_min === selected.value?.duration_min && option.resource_id === selected.value?.resource_id))) {
      selected.value = null
    }
  } catch {
    if (sequence === requestSequence) error.value = true
  } finally {
    if (sequence === requestSequence) {
      loading.value = false
      refreshing.value = false
    }
  }
}

function updateFilter(key: string, value: string) {
  if (key === 'sport') {
    delete filters.value.indoor
    delete filters.value.court_type
  }
  if (filters.value[key] === value && key !== 'date') delete filters.value[key]
  else filters.value[key] = value
  selected.value = null
  delete filters.value.start
  const pageUrl = new URL(window.location.href)
  ;['date', 'sport', 'duration', 'indoor', 'court_type'].forEach((name) => {
    const filter = filters.value[name]
    if (filter) pageUrl.searchParams.set(name, filter)
    else pageUrl.searchParams.delete(name)
  })
  window.history.replaceState({}, '', pageUrl)
  void load()
}

function choose(start: Start, option: Option) {
  selected.value = { ...option, start: start.start, time: start.time }
}

function dateLabel(date: string) {
  return new Intl.DateTimeFormat(locale.value, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${date}T12:00:00Z`))
}

function visibilityRefresh() {
  if (document.visibilityState === 'visible') void load(true)
}

onMounted(() => {
  void load()
  timer = window.setInterval(() => void load(true), 60_000)
  document.addEventListener('visibilitychange', visibilityRefresh)
})
onBeforeUnmount(() => {
  requestController?.abort()
  if (timer) window.clearInterval(timer)
  document.removeEventListener('visibilitychange', visibilityRefresh)
})
</script>

<template>
  <div class="availability-shell rounded-3xl border border-base-300 bg-base-100 p-4 shadow-sm sm:p-6">
    <div v-if="loading" class="grid gap-3" aria-live="polite">
      <div class="skeleton h-10 w-3/4" /><div class="skeleton h-28 w-full" /><div class="skeleton h-28 w-full" />
    </div>
    <div v-else-if="error" class="py-10 text-center">
      <p class="font-bold">{{ t('player.availability.error') }}</p>
      <button type="button" class="btn btn-outline mt-4" @click="load()">{{ t('player.availability.retry') }}</button>
    </div>
    <template v-else-if="payload">
      <div class="flex flex-wrap gap-2">
        <button v-for="category in payload.categories" :key="category.value" type="button" class="filter-chip" :class="{ active: filters.sport === category.value || (!filters.sport && payload.categories[0]?.value === category.value) }" @click="updateFilter('sport', category.value)">{{ category.label }}</button>
      </div>
      <div v-if="payload.filters.durations.length > 1" class="mt-3 flex flex-wrap gap-2">
        <button v-for="duration in payload.filters.durations" :key="duration" type="button" class="filter-chip" :class="{ active: filters.duration === String(duration) }" @click="updateFilter('duration', String(duration))">{{ t('player.availability.minutes', { count: duration }) }}</button>
      </div>
      <div v-if="payload.filters.indoors.length > 1" class="mt-3 flex flex-wrap gap-2">
        <button type="button" class="filter-chip" :class="{ active: filters.indoor === 'true' }" @click="updateFilter('indoor', 'true')">{{ t('player.availability.indoor') }}</button>
        <button type="button" class="filter-chip" :class="{ active: filters.indoor === 'false' }" @click="updateFilter('indoor', 'false')">{{ t('player.availability.outdoor') }}</button>
      </div>
      <div v-if="payload.filters.court_types.length > 1" class="mt-3 flex flex-wrap gap-2">
        <button v-for="courtType in payload.filters.court_types" :key="courtType" type="button" class="filter-chip" :class="{ active: filters.court_type === courtType }" @click="updateFilter('court_type', courtType)">{{ t(`player.availability.court_types.${courtType}`) }}</button>
      </div>

      <div class="date-strip mt-6 flex gap-2 overflow-x-auto pb-2">
        <button v-for="day in payload.dates" :key="day.date" type="button" class="date-pill" :class="{ active: day.date === payload.selected_date, muted: !day.available }" @click="updateFilter('date', day.date)">{{ dateLabel(day.date) }}</button>
      </div>
      <div class="mt-5 flex items-center justify-between gap-3">
        <div class="join"><button type="button" class="btn btn-sm join-item" :class="{ 'btn-neutral': mode === 'any' }" @click="mode = 'any'">{{ t('player.availability.any_court') }}</button><button type="button" class="btn btn-sm join-item" :class="{ 'btn-neutral': mode === 'resource' }" @click="mode = 'resource'">{{ t('player.availability.choose_court') }}</button></div>
        <span v-if="refreshing" class="loading loading-spinner loading-sm" :aria-label="t('common.loading')" />
      </div>

      <div v-if="payload.starts.length" class="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <article v-for="start in payload.starts" :key="start.start" class="slot-card rounded-2xl border border-base-300 p-4">
          <p class="text-2xl font-black">{{ start.time_label }}</p>
          <div class="mt-3 grid gap-2">
            <button v-for="option in mode === 'any' ? start.any_options : start.options" :key="`${option.resource_id}-${option.duration_min}`" type="button" class="slot-option" @click="choose(start, option)">
              <span><strong>{{ option.duration_min }} {{ t('player.availability.min_short') }}</strong><small v-if="mode === 'resource'">{{ option.resource_name }}</small><small v-if="option.shared">{{ t('player.availability.places_left', { count: option.remaining_seats }) }}</small></span>
              <span class="text-right"><strong>{{ money(option.price_cents) }}</strong><small v-if="option.per_player_cents">≈ {{ money(option.per_player_cents) }} {{ t('player.availability.per_player') }}</small></span>
            </button>
          </div>
        </article>
      </div>
      <div v-else class="mt-6 rounded-2xl bg-base-200 p-7 text-center"><p class="font-bold">{{ t('player.availability.empty') }}</p><button v-if="payload.nearest" type="button" class="btn btn-link mt-2" @click="updateFilter('date', payload.nearest.date)">{{ t('player.availability.nearest', { date: dateLabel(payload.nearest.date), time: payload.nearest.time }) }}</button></div>
      <p class="mt-5 text-sm text-base-content/55">{{ t('player.availability.venue_time', { zone: payload.venue.time_zone }) }}</p>

      <div v-if="selected" class="sticky bottom-20 z-20 mt-7 flex flex-col gap-4 rounded-2xl bg-neutral p-4 text-neutral-content shadow-2xl sm:bottom-4 sm:flex-row sm:items-center sm:justify-between">
        <div><p class="font-black">{{ dateLabel(payload.selected_date) }}, {{ selected.time }} · {{ selected.duration_min }} {{ t('player.availability.min_short') }}</p><p class="text-sm text-neutral-content/70">{{ selected.any_resource ? t('player.availability.any_court') : selected.resource_name }} · {{ money(selected.price_cents) }}</p></div>
        <a :href="checkoutUrl" class="btn venue-action">{{ t('player.availability.book') }}</a>
      </div>
    </template>
  </div>
</template>
